/**
 * useContentComments
 *
 * Orchestration hook for the content comments sheet.
 * Mirrors Flutter's comments_bottom_sheet.dart state & flows.
 *
 * Responsibilities:
 *   - load root comments (paginated)
 *   - expand replies per parent
 *   - add comment / reply (optimistic + transactional write)
 *   - like / unlike comment
 *   - deep-link highlight via CommentInfo
 *   - keep list in sync via eventBus (COMMENT_ADDED)
 *
 * The live video-reply media watchers are owned by `useCommentMediaWatchers`;
 * this hook only re-exports its `watchCommentMedia` on the result.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import { fetchRootCommentsUseCase } from '@/domain/usecases/comment/fetchRootCommentsUseCase';
import { fetchChildCommentsUseCase } from '@/domain/usecases/comment/fetchChildCommentsUseCase';
import { fetchNotificationCommentsUseCase } from '@/domain/usecases/comment/fetchNotificationCommentsUseCase';
import { addCommentUseCase } from '@/domain/usecases/comment/addCommentUseCase';
import { likeCommentUseCase } from '@/domain/usecases/comment/likeCommentUseCase';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { isVideoReplyAvailable } from '@/domain/utils/commentUtils';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useCommentMediaWatchers } from './useCommentMediaWatchers';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';
import type { CommentInfoEntity } from '@/domain/entities/comment/commentInfoEntity';
import type { MediaInfoEntity } from '@/domain/entities/info/mediaInfoEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';
import type { CommentLikeDto } from '@/data/dtos/commentLike/commentLikeDto';
import type { RootState } from '@/presentation/store';

export interface UseContentCommentsParams {
  media: UserMediaEntity;
  commentInfo?: CommentInfoEntity;
}

/**
 * Overrides for `addComment`, mirroring Flutter's
 * `_addComment({String? commentId, MediaInfo? replyMediaInfo})`
 * (comments_bottom_sheet.dart:422-440).
 *
 * Presentation-local on purpose: this shape never crosses a layer boundary —
 * the hook unpacks it into the `CommentEntity` it hands to
 * `addCommentUseCase`, so it stays next to its only consumer rather than under
 * `domain/input/`.
 */
export interface AddCommentOptions {
  /**
   * Pre-minted comment id. Flutter `_addComment({String? commentId})`
   * (comments_bottom_sheet.dart:466) — the video reply mints it BEFORE the
   * upload so the transcode payload and the comment doc agree on the id the
   * server later looks the comment up by.
   */
  commentId?: string;
  /**
   * Placeholder media info for a video reply. Flutter `replyMediaInfo`
   * (comments_bottom_sheet.dart:480); when set, the empty-text bail-out is
   * skipped (:438), i.e. a media reply may be posted with no text at all.
   */
  mediaInfo?: MediaInfoEntity | null;
  /**
   * Reply target captured at tap time. Flutter re-reads the notifier that
   * `didPopNext` restored (:182, :446) — net effect: the target in force is the
   * one captured when the add-media button was tapped. The web sheet stays
   * mounted under the stacked add-content sheet, so the caller passes that
   * captured target explicitly instead. Falls back to the live
   * `commentToReply` when omitted.
   */
  replyTo?: CommentEntity | null;
}

export interface UseContentCommentsResult {
  comments: CommentEntity[];
  isLoading: boolean;
  hasMore: boolean;
  commentCount: number;
  commentToReply: CommentEntity | null;
  highlightedCommentId: string | null;
  videoReplyIsAvailable: boolean;
  loadMore: () => Promise<void>;
  expandReplies: (parent: CommentEntity) => Promise<void>;
  collapseReplies: (parent: CommentEntity) => void;
  addComment: (text: string, options?: AddCommentOptions) => Promise<boolean>;
  /** Re-exported from `useCommentMediaWatchers` — see its JSDoc. */
  watchCommentMedia: (commentId: string) => void;
  likeComment: (comment: CommentEntity, liked: boolean) => Promise<void>;
  setCommentToReply: (comment: CommentEntity | null) => void;
  cancelReply: () => void;
}

export function useContentComments(
  params: UseContentCommentsParams,
): UseContentCommentsResult {
  const { media, commentInfo } = params;
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [comments, setComments] = useState<CommentEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [commentToReply, setCommentToReply] = useState<CommentEntity | null>(null);
  const [commentCount, setCommentCount] = useState<number>(media.comments ?? 0);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);

  // Mirror the live count in a ref so the fan-out emits the same value the title
  // lands on, even across multiple adds within one sheet session. Reading the
  // commentCount state captured in addComment's closure would be stale; the ref
  // is the single source of truth shared with the title.
  const commentCountRef = useRef(commentCount);
  useEffect(() => { commentCountRef.current = commentCount; }, [commentCount]);

  const lastFetchedRef = useRef<PageCursor | undefined>(undefined);
  const mountedRef = useRef(true);
  const loadMoreInFlightRef = useRef(false);
  const expandInFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Live video-reply media listeners own their own registry + teardown.
  const { watchCommentMedia } = useCommentMediaWatchers(setComments);

  const mediaPath = media.relativePath;

  const loadInitial = useCallback(async () => {
    setIsLoading(true);

    try {
      if (commentInfo?.id) {
        const notifComments = await fetchNotificationCommentsUseCase.execute(commentInfo);
        if (!mountedRef.current) return;
        const parent = notifComments.find(c => c.id === commentInfo.parentId);
        const target = notifComments.find(c => c.id === commentInfo.id);
        if (parent) {
          parent.children = notifComments.filter(c => c.parentId === parent.id);
          parent.hasMoreChildren = true;

          setComments([parent]);
          setHighlightedCommentId(
            commentInfo.highlightParentComment ? parent.id : (target?.id ?? parent.id),
          );
        } else if (target) {
          setComments([target]);
          setHighlightedCommentId(target.id);
        }
      }

      const result = await fetchRootCommentsUseCase.execute(mediaPath);
      if (!mountedRef.current) return;

      setComments(prev => {
        // Merge: keep any deep-linked comment already in state first, then append paged results
        const byId = new Map<string, CommentEntity>();
        for (const c of prev) byId.set(c.id, c);
        for (const c of result.comments) if (!byId.has(c.id)) byId.set(c.id, c);
        return Array.from(byId.values());
      });
      
      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading comments', 'useContentComments.loadInitial', { error: error as Error });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPath]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Keep counter in sync with other sheets/instances.
  // Every comment add (root OR reply) bumps the content-level count, matching
  // Flutter (comments_bottom_sheet.dart:509-514 increments media.comments with
  // no parentId gate) and the server-side increment in
  // firestoreService.addComment / content_reactions_data_source.dart:453.
  useEffect(() => {
    const offAdd = eventBus.on(APP_EVENT_KEYS.COMMENT_ADDED, data => {
      if (data.mediaId === media.id) {
        setCommentCount(c => c + 1);
      }
    });

    return () => { offAdd(); };
  }, [media.id]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;

    setIsLoading(true);

    try {
      const result = await fetchRootCommentsUseCase.execute(mediaPath, lastFetchedRef.current);

      if (!mountedRef.current) return;

      setComments(prev => {
        const byId = new Map<string, CommentEntity>();
        for (const c of prev) byId.set(c.id, c);
        for (const c of result.comments) if (!byId.has(c.id)) byId.set(c.id, c);
        return Array.from(byId.values());
      });

      setHasMore(result.hasMore);
      lastFetchedRef.current = result.cursor;
    } catch (error) {
      appLogger.error('Error loading more comments', 'useContentComments.loadMore', { error: error as Error });
    } finally {
      loadMoreInFlightRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, [hasMore, mediaPath]);

  const expandReplies = useCallback(async (parent: CommentEntity) => {
    if (expandInFlightRef.current.has(parent.id)) return;
    expandInFlightRef.current.add(parent.id);

    setComments(prev => prev.map(c =>
      c.id === parent.id ? { ...c, isFetchingChildren: true } : c,
    ));

    try {
      const result = await fetchChildCommentsUseCase.execute(
        mediaPath,
        parent.id,
        parent.lastFetchedChild ?? undefined,
      );

      if (!mountedRef.current) return;

      setComments(prev => prev.map(c => {
        if (c.id !== parent.id) return c;
        const existing = c.children ?? [];
        const byId = new Map<string, CommentEntity>();
        for (const child of existing) byId.set(child.id, child);
        for (const child of result.comments) if (!byId.has(child.id)) byId.set(child.id, child);
        return {
          ...c,
          children: Array.from(byId.values()),
          hasMoreChildren: result.hasMore,
          lastFetchedChild: result.cursor ?? null,
          isFetchingChildren: false,
        };
      }));
    } catch (error) {
      appLogger.error('Error expanding replies', 'useContentComments.expandReplies', { error: error as Error });
      if (mountedRef.current) {
        setComments(prev => prev.map(c =>
          c.id === parent.id ? { ...c, isFetchingChildren: false } : c,
        ));
      }
    } finally {
      expandInFlightRef.current.delete(parent.id);
    }
  }, [mediaPath]);

  const buildNotificationInfo = useCallback(
    (parentId: string | null, replyId: string | null, peerUserId: string, peerName: string, commentId: string, notificationType: number): NotificationInfoDto | null => {
      if (!currentUser) return null;

      return {
        usersInfo: {
          userId: currentUser.firebaseId,
          peerUserId,
          name: currentUser.name,
          username: currentUser.username,
          peerName,
        },
        mediaInfo: {
          firebaseId: media.firebaseId ?? '',
          path: media.relativePath,
          thumbnail: media.relativeThumbnailPath ?? '',
          duration: media.duration,
          type: media.type,
          aspectRatio: media.aspectRatio,
          allowEarlyAccess: media.allowEarlyAccess,
        },
        commentInfo: { id: commentId, parentId: parentId ?? null, replyId: replyId ?? null },
        notificationType,
        read: false,
        isUndo: false,
        timestamp: Date.now(),
      };
    },
    [currentUser, media],
  );

  const addComment = useCallback(async (
    text: string,
    options?: AddCommentOptions,
  ): Promise<boolean> => {
    const trimmed = text.trim();
    if (!currentUser) return false;
    // Flutter comments_bottom_sheet.dart:438 — a media reply may be posted with
    // empty text; only the text-only path requires non-blank input.
    if (!options?.mediaInfo && !trimmed) return false;

    try {
      // Flutter :466 `commentId ?? const Uuid().v1()` — the video reply mints
      // the id up front so the transcode payload can reference it.
      const commentId = options?.commentId ?? generateUuidV1();
      // Flutter re-reads the reply-target notifier that didPopNext restored
      // (:182, :446); web passes the target captured at tap time instead.
      const target = options?.replyTo ?? commentToReply;
      const parentId = target?.parentId ?? target?.id ?? null;
      const isReplyToReply = !!target?.parentId;
      const replyToReply = isReplyToReply && target
        ? { replyId: target.id, userName: target.userName, userId: target.userId }
        : null;

      const comment: CommentEntity = {
        id: commentId,
        parentId: parentId,
        text: trimmed,
        commentToReply: replyToReply,
        replies: 0,
        likes: 0,
        popularity: 0,
        createdTimestamp: Date.now(),
        mediaPath: media.relativePath,
        userId: currentUser.firebaseId,
        contentUserId: media.firebaseId ?? '',
        userName: currentUser.name,
        username: currentUser.username,
        mediaInfo: options?.mediaInfo ?? null,
        children: [],
        hasMoreChildren: false,
        childrenFetched: 0,
        isFetchingChildren: false,
      };

      const peerUserId = target?.userId ?? media.firebaseId ?? '';
      const peerName = target?.userName ?? media.userName ?? '';
      const notificationType = parentId
        ? AppNotificationType.commentReply
        : AppNotificationType.contentComment;

      const notification = buildNotificationInfo(
        parentId,
        replyToReply?.replyId ?? null,
        peerUserId,
        peerName,
        commentId,
        notificationType,
      );

      // Optimistic insert
      setComments(prev => {
        if (!parentId) return [comment, ...prev];
        return prev.map(c => {
          if (c.id !== parentId) return c;
          return {
            ...c,
            replies: c.replies + 1,
            children: [...(c.children ?? []), comment],
          };
        });
      });

      const success = await addCommentUseCase.execute({
        comment,
        authorUserId: currentUser.firebaseId,
        sendCommentNotification: peerUserId !== currentUser.firebaseId,
        notification,
      });

      if (!success) {
        // Roll back optimistic insert
        setComments(prev => {
          if (!parentId) return prev.filter(c => c.id !== commentId);
          return prev.map(c => {
            if (c.id !== parentId) return c;
            return {
              ...c,
              replies: Math.max(0, c.replies - 1),
              children: (c.children ?? []).filter(ch => ch.id !== commentId),
            };
          });
        });
        return false;
      }

      eventBus.emit(APP_EVENT_KEYS.COMMENT_ADDED, {
        mediaId: media.id,
        commentId,
        parentId,
      });

      // Every comment add (root OR reply) bumps the content-level comments
      // count, matching Flutter (comments_bottom_sheet.dart:509-514 runs the
      // increment with no parentId gate) and the server-side increment in
      // firestoreService.addComment / content_reactions_data_source.dart:453.
      // The sheet's own title increment is driven solely by the COMMENT_ADDED
      // self-listener above; do not increment commentCount directly here.
      // Compute the new count from the live counter ref (not the frozen
      // media.comments prop) so consecutive adds in one session emit the value
      // the title actually lands on (11, 12, …) rather than a stale duplicate.
      const newCount = commentCountRef.current + 1;
      eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, {
        mediaId: media.id,
        comments: newCount,
      });

      setCommentToReply(null);
      return true;
    } catch (error) {
      appLogger.error('Error adding comment', 'useContentComments.addComment', { error: error as Error });
      return false;
    }
  }, [buildNotificationInfo, commentToReply, currentUser, media]);

  const likeComment = useCallback(async (comment: CommentEntity, liked: boolean) => {
    if (!currentUser) return;
    if (currentUser.firebaseId === comment.userId) return;

    // Optimistic toggle
    const delta = liked ? 1 : -1;
    setComments(prev => prev.map(c => {
      if (c.id === comment.id) return { ...c, likes: Math.max(0, c.likes + delta) };
      if (c.children?.some(ch => ch.id === comment.id)) {
        return {
          ...c,
          children: c.children.map(ch =>
            ch.id === comment.id ? { ...ch, likes: Math.max(0, ch.likes + delta) } : ch,
          ),
        };
      }
      return c;
    }));

    const like: CommentLikeDto = {
      userId: currentUser.firebaseId,
      commentId: comment.id,
      parentCommentId: comment.parentId ?? undefined,
      liked,
      createdDate: Date.now(),
    };

    const notification = buildNotificationInfo(
      comment.parentId ?? null,
      null,
      comment.userId,
      comment.userName ?? '',
      comment.id,
      AppNotificationType.commentLike,
    );

    if (!notification) return;

    try {
      const success = await likeCommentUseCase.execute({ like, notification });

      if (!success) {
        // Rollback
        setComments(prev => prev.map(c => {
          if (c.id === comment.id) return { ...c, likes: Math.max(0, c.likes - delta) };
          if (c.children?.some(ch => ch.id === comment.id)) {
            return {
              ...c,
              children: c.children.map(ch =>
                ch.id === comment.id ? { ...ch, likes: Math.max(0, ch.likes - delta) } : ch,
              ),
            };
          }
          return c;
        }));
      }
    } catch (error) {
      appLogger.error('Error liking comment', 'useContentComments.likeComment', { error: error as Error });
    }
  }, [buildNotificationInfo, currentUser]);

  const collapseReplies = useCallback((parent: CommentEntity) => {
    setComments(prev => prev.map(c =>
      c.id === parent.id
        ? { ...c, children: [], hasMoreChildren: false, lastFetchedChild: null, isFetchingChildren: false }
        : c,
    ));
  }, []);

  const cancelReply = useCallback(() => setCommentToReply(null), []);

  // Flutter `_videoReplyIsAvailable()` (comments_bottom_sheet.dart:101-116);
  // the rule itself lives in the domain helper.
  const videoReplyIsAvailable = useMemo(
    () => isVideoReplyAvailable(media, currentUser, commentToReply),
    [media, currentUser, commentToReply],
  );

  return {
    comments,
    isLoading,
    hasMore,
    commentCount,
    commentToReply,
    highlightedCommentId,
    videoReplyIsAvailable,
    loadMore,
    expandReplies,
    collapseReplies,
    addComment,
    watchCommentMedia,
    likeComment,
    setCommentToReply,
    cancelReply,
  };
}
